import { useQuery, useReactiveVar } from '@apollo/client'

import { BroadFulfilmentMethodInputType } from '@src/graphql-types'

import { BasketOutletMenuItems } from './queries/__generated__/BasketOutletMenuItems'
import {
  CheckoutBasketTotalsDocument,
  CheckoutBasketTotalsQuery,
} from './queries/__generated__/checkoutBasketTotals.graphql-interface'
import { getBroadFulfilmentInputFromOutletFulfilment } from './utils/getBroadFulfilmentInputFromOutletFulfilment'
import { paymentMethodVar } from './utils/paymentMethodReactiveVar'

import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '../outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { BasketItem } from '../useBasketItems/validation'
import { useDiscounts } from '../useDiscounts/useDiscounts'
import { useBasketItemTotals } from '../useBasketItemTotals'

export type UseBasketTotalsResponseData = {
  checkoutBasketTotals: CheckoutBasketTotalsQuery['checkoutBasketTotals'] | null
  basketItemsWithPrices: BasketItemWithPrices[]
  basketItemsTotal: number
}

export type BasketOutletMenuItem =
  BasketOutletMenuItems['outletMenuItems'][number]
export type BasketItemWithPrices = {
  basketItem: BasketItem
  outletMenuItem: BasketOutletMenuItem
  selectedOptionItems: BasketOutletMenuItem['options'][number]['optionItems']
  isAddOnItem: boolean
  // price of the menu item with the selected options
  totalPriceForOneItem: number
  // price of the menu item with the selected options multiplied by the quantity
  totalPrice: number
}

// hook which uses the data from the basket and fulfilment models (ie persisted global state) to calculate:
// the basket total, the fulfilment total, the service charge, any discounts and the sum total
// returns null if the basket is empty
export const useBasketTotals = ({
  addressIdOverride,
  skip = false,
}: {
  addressIdOverride?: string
  skip?: boolean
} = {}): {
  data: UseBasketTotalsResponseData
  loading: boolean
  error: any
} => {
  const outletFulfilment = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })
  const { discounts } = useDiscounts()
  const basketItemTotalsResponse = useBasketItemTotals()

  const paymentMethod = useReactiveVar(paymentMethodVar)

  const broadFulfilmentInput = addressIdOverride
    ? {
        broadFulfilmentMethod: BroadFulfilmentMethodInputType.ADDRESS,
        addressId: addressIdOverride,
      }
    : getBroadFulfilmentInputFromOutletFulfilment(
        outletFulfilment.data.currentFulfilment,
        outletFulfilment.outlet.isPendingTableNumbersEnabled
      )

  // TODO: handle refetching basket on expiryAt
  const checkoutBasketTotalsQueryResponse = useQuery(
    CheckoutBasketTotalsDocument,
    broadFulfilmentInput &&
      basketItemTotalsResponse.data &&
      basketItemTotalsResponse.data.basketItemsWithPrices.length > 0 &&
      !skip
      ? {
          variables: {
            basketData: {
              outletId: outletFulfilment.outlet.id,
              nonAddOnBasketItemsTotal:
                basketItemTotalsResponse.data.nonAddOnBasketItemsTotal,
              addOnBasketItemsTotal:
                basketItemTotalsResponse.data.addOnBasketItemsTotal,
              fulfilment: broadFulfilmentInput,
              voucherDiscountId: discounts.voucherDiscountId,
              paymentMethod: paymentMethod.detailedPaymentMethod,
            },
          },
          fetchPolicy: 'network-only',
        }
      : { skip: true }
  )

  if (skip) {
    return {
      data: {
        checkoutBasketTotals: null,
        basketItemsWithPrices: [],
        basketItemsTotal: 0,
      },
      loading: false,
      error: null,
    }
  }

  const {
    basketItemsWithPrices,
    nonAddOnBasketItemsTotal,
    addOnBasketItemsTotal,
  } = basketItemTotalsResponse.data || {
    basketItemsWithPrices: [],
    nonAddOnBasketItemsTotal: 0,
    addOnBasketItemsTotal: 0,
  }
  const basketItemsTotal = nonAddOnBasketItemsTotal + addOnBasketItemsTotal

  if (
    basketItemTotalsResponse.loading ||
    checkoutBasketTotalsQueryResponse.loading
  ) {
    return {
      data: {
        checkoutBasketTotals:
          checkoutBasketTotalsQueryResponse.previousData
            ?.checkoutBasketTotals || null,
        basketItemsWithPrices,
        basketItemsTotal,
      },
      loading: true,
      error: null,
    }
  }

  const newTotals: UseBasketTotalsResponseData = {
    checkoutBasketTotals:
      checkoutBasketTotalsQueryResponse.data?.checkoutBasketTotals || null,
    basketItemsWithPrices,
    basketItemsTotal,
  }

  return {
    data: newTotals,
    loading: false,
    error: checkoutBasketTotalsQueryResponse.error,
  }
}
